<template>
  <SearchLayout
    v-model:filter-open="filterOpen"
    :map-visible="mapVisible"
    :total="total"
  >
    <template #pageHeader>
      <PageHeader :title="title" />
    </template>
    <template #sidebar>
      <SearchSearchTerm />
      <SearchCategoryFilter
        v-if="categories.length"
        :terms="categories"
        :facets="facets"
      />
      <SearchLocationFilter
        v-if="locations.length && hasLocationFilter"
        :locations="locations"
      />
      <Component
        :is="getFacetComponent(facet.key)"
        v-for="facet in visibleFacets"
        :key="facet.key"
        :terms="facet.terms"
        :facet-key="facet.key"
        :label="facet.label"
      />
      <SearchFacetRangeCheckbox
        v-if="hasCurrentlyOpenFilter"
        query="open"
        :label="$texts('currentlyOpen')"
      />

      <SearchFacetRangeCheckbox
        v-if="hasSpecialOffersFilter"
        query="specialOffers"
        :label="$texts('search.hasSpecialOffers')"
      />
    </template>
    <template #resultsHeader>
      <SearchResultsHeader
        v-model:filter-open="filterOpen"
        :has-map="hasMap"
        :total="total"
      />
    </template>
    <template #selected>
      <SearchSelected :facets="facets" :location-filters="locations" />
    </template>
    <template #results>
      <SearchResultsMap v-if="mapVisible" :results="hits" @close="closeMap" />
      <div v-else class="search-page">
        <SearchResults
          :items="hits"
          :has-map="hasMap"
          :show-opening-hours-status="hasCurrentlyOpenFilter"
        />
      </div>
      <SearchPagination
        v-if="!mapVisible"
        :page="page"
        :per-page="perPage"
        :total="total"
      />
    </template>
  </SearchLayout>
</template>

<script setup lang="ts">
import { DomainId } from '#graphql-operations'
import type { SearchFacet } from '~~/layers/legacy-search/app/types'
import { HIDDEN_FACETS } from '~~/layers/legacy-search/app/settings'
import SearchFacetFull from './../Facet/index.vue'
import SearchFacetSingle from './../Facet/Single/index.vue'
import { getFilterCategoryColor } from '~~/layers/legacy-search/app/helpers/search'

const route = useRoute()
const router = useRouter()
const language = useCurrentLanguage()

const props = defineProps<{
  title: string
  searchId: string
  hasMap?: boolean
  hasLocationFilter?: boolean
  hasCurrentlyOpenFilter?: boolean
  hasSpecialOffersFilter?: boolean
}>()

const filterOpen = ref(false)

const mapVisible = computed({
  get() {
    return route.query.map === 'true'
  },
  set(isVisible: boolean) {
    router.push({
      query: {
        ...route.query,
        map: isVisible ? 'true' : undefined,
      },
    })
  },
})

const { locations, getTermData, getFacetLabel } = await useSearchData()
const { activeDomain } = useSiteContext()

const text = useQueryString('text')
const queryFilterCategory = useQueryString('category')
const queryFacets = computed<string>(() => {
  const value = route.query.f
  if (Array.isArray(value)) {
    return value.filter((v) => typeof v === 'string').join('___')
  } else if (typeof value === 'string') {
    return value
  }

  return ''
})

const queryPage = useQueryString('page')
const querySort = useQueryString('sort')
const queryLocationId = useQueryString('location')
const queryCurrentlyOpen = useQueryString('open')
const querySpecialOffers = useQueryString('specialOffers')
const queryMap = useQueryString('map')

const query = computed(() => {
  const facets = queryFacets.value.split('___')

  if (activeDomain === DomainId.hoteljobs_zuerich_ch) {
    facets.push('accommodation_type:76')
  }

  const location = locations.value.find((v) => v.id === queryLocationId.value)
  const latlon = location ? [location.lat, location.lon].join(',') : undefined
  const radius = location?.radius

  return {
    category: queryFilterCategory.value,
    facets,
    language: language.value,
    text: text.value,
    map: queryMap.value === 'true' || undefined,
    domain: activeDomain,
    page: queryPage.value || undefined,
    sort: querySort.value || undefined,
    id: props.searchId,
    latlon,
    radius,
    currentlyOpen: queryCurrentlyOpen.value === 'true' || undefined,
    specialOffers: querySpecialOffers.value === 'true' || undefined,
  }
})

const { data } = await useFetch('/api/search/places', {
  query,
  deep: false,
  dedupe: 'defer',
})

const facets = computed<SearchFacet[]>(() => {
  return (
    (data.value?.facets || []).map((v) => {
      return {
        key: v.key,
        label: getFacetLabel(v.key) || v.key,
        terms: v.terms.map((term) => {
          const data = getTermData(term.key)
          return {
            key: term.key,
            label: data?.label || term.key,
            count: term.count,
            active: term.active,
            weight: data?.weight || 0,
          }
        }),
      }
    }) || []
  )
})

const visibleFacets = computed(() => {
  return facets.value.filter((v) => {
    if (
      activeDomain === DomainId.hoteljobs_zuerich_ch &&
      v.key === 'accommodation_type'
    ) {
      return false
    }
    return !HIDDEN_FACETS.includes(v.key)
  })
})

const categories = computed(() => {
  return (data.value?.categories || []).map((v) => {
    return {
      ...v,
      label: v.key,
      weight: 0,
    }
  })
})

function getFacetComponent(key: string) {
  switch (key) {
    case 'bookability':
    case 'at_the_lake':
    case 'sustainability':
    case 'has_virtual_tour':
      return SearchFacetSingle
  }

  return SearchFacetFull
}

const hits = computed(() => data.value?.hits || [])
const perPage = computed(() => data.value?.perPage)
const page = computed(() => data.value?.page)
const total = computed(() => data.value?.total || 0)

const selectedCategory = computed(() => {
  if (!queryFilterCategory.value) {
    return null
  }

  const top = getFacetLabel(queryFilterCategory.value)

  if (!top) {
    return null
  }

  const color = getFilterCategoryColor(queryFilterCategory.value)

  if (!color) {
    return null
  }

  return {
    top,
    color,
  }
})

const { setLogoOverride } = useLogo()

function updateCategoryLogo() {
  setLogoOverride(selectedCategory.value)
}

updateCategoryLogo()
watch(selectedCategory, updateCategoryLogo)

function closeMap() {
  router.push({
    query: {
      ...route.query,
      map: undefined,
      center: undefined,
      nid: undefined,
      zoom: undefined,
    },
  })
}

onBeforeUnmount(() => {
  setLogoOverride()
})
</script>
